import React from 'react';

const PrivacyPolicy = ({ onBack, embedded }) => {
  const s = {
    container: {
      minHeight: '100vh',
      backgroundColor: '#f0f4f8',
      color: '#1a2e4a',
      padding: '2rem',
      fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Inter", sans-serif',
    },
    wrapper: { maxWidth: '860px', margin: '0 auto' },
    header: { marginBottom: '2rem', paddingBottom: '1.5rem', borderBottom: '1px solid #c8d8e8' },
    backLink: {
      display: 'inline-block', marginBottom: '1.5rem', color: '#0EA5E9',
      textDecoration: 'none', fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer',
    },
    title: { fontSize: '2.2rem', fontWeight: 700, margin: '0 0 0.4rem', color: '#0A1B3A' },
    subtitle: { fontSize: '0.9rem', color: '#5a7a9a', margin: 0 },
    sectionTitle: {
      fontSize: '1.1rem', fontWeight: 700, color: '#0EA5E9',
      marginBottom: '0.75rem', marginTop: '2rem', textTransform: 'uppercase', letterSpacing: '0.04em',
    },
    p: { marginBottom: '0.9rem', color: '#2a4060', fontSize: '0.93rem', lineHeight: 1.75 },
    li: { marginLeft: '1.5rem', marginBottom: '0.6rem', color: '#2a4060', fontSize: '0.93rem', lineHeight: 1.7 },
    em: { color: '#0EA5E9', fontWeight: 600 },
    box: {
      backgroundColor: '#e6f2fb', border: '1px solid #b3d4eb', borderRadius: 10,
      padding: '1.25rem 1.5rem', margin: '1rem 0', fontSize: '0.93rem',
    },
    grid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginTop: '0.75rem' },
    gridItem: {
      background: '#fff', border: '1px solid #c8d8e8', borderRadius: 8,
      padding: '0.75rem 1rem', fontSize: '0.88rem', color: '#2a4060',
    },
    gridLabel: { fontSize: '0.75rem', color: '#5a7a9a', fontWeight: 700, textTransform: 'uppercase', marginBottom: 3 },
    footer: {
      marginTop: '3rem', paddingTop: '1.5rem', borderTop: '1px solid #c8d8e8',
      fontSize: '0.82rem', color: '#7a99b8', textAlign: 'center',
    },
  };

  const inner = (
    <div style={embedded ? {} : s.wrapper}>
      {!embedded && (
        <div style={s.header}>
          <span style={s.backLink} onClick={onBack || (() => window.history.back())}>← Back</span>
          <h1 style={s.title}>Privacy Policy</h1>
          <p style={s.subtitle}>Effective: April 2026 &nbsp;·&nbsp; Mancini Business Consulting LLC</p>
        </div>
      )}
      {embedded && (
        <div style={{ marginBottom: '1.5rem', paddingBottom: '1rem', borderBottom: '1px solid #c8d8e8' }}>
          <h2 style={s.title}>Privacy Policy</h2>
          <p style={s.subtitle}>Effective: April 2026 &nbsp;·&nbsp; Mancini Business Consulting LLC</p>
        </div>
      )}

        <p style={s.p}>
          Mancini Business Consulting LLC ("we," "us," or "Company") operates the Vella AI marketing
          platform at <strong>vella.amplifx.com</strong>. This Policy explains what personal data we
          collect, how we use it, who we share it with, and your rights under GDPR and CCPA.
        </p>

        <h2 style={s.sectionTitle}>1. Data We Collect</h2>
        <ul>
          <li style={s.li}><span style={s.em}>Account data</span> — name, email, business name, phone number.</li>
          <li style={s.li}><span style={s.em}>Voice recordings</span> — when you use "Talk to Vella," your microphone audio is streamed to ElevenLabs and/or Anthropic for real-time transcription and AI response. Recordings are not stored by us beyond the session unless you explicitly save a transcript.</li>
          <li style={s.li}><span style={s.em}>AI prompt &amp; content data</span> — text prompts, generated marketing copy, campaign briefs, and creative outputs you create through the platform.</li>
          <li style={s.li}><span style={s.em}>SMS campaign data</span> — recipient phone numbers, message content, delivery status, and opt-out records.</li>
          <li style={s.li}><span style={s.em}>Payment data</span> — processed by Stripe. We store only the last 4 digits of your card and billing address. Full card numbers never touch our servers.</li>
          <li style={s.li}><span style={s.em}>Usage &amp; technical data</span> — IP address, browser type, page views, and feature interactions collected via Supabase.</li>
        </ul>

        <h2 style={s.sectionTitle}>2. Voice Recording Disclosure</h2>
        <div style={s.box}>
          <strong>By using the voice interface ("Talk to Vella"), you consent to audio capture.</strong> Your voice is streamed in real time to ElevenLabs (for voice synthesis) and Anthropic (for AI reasoning). We do not retain raw audio recordings on our servers. ElevenLabs and Anthropic process audio under their own privacy policies and data processing agreements. You can disable microphone access at any time in your browser or device settings.
        </div>

        <h2 style={s.sectionTitle}>3. AI Content Generation Disclosure</h2>
        <p style={s.p}>
          Vella uses large language models operated by Anthropic and OpenAI to generate marketing content, analyze data, and power chat interactions. Prompts and generated content you submit may be processed on Anthropic and OpenAI servers. Neither provider uses your data to train their publicly released models under our enterprise agreements. Do not submit sensitive personal information (SSNs, passwords, financial account numbers) in prompts.
        </p>

        <h2 style={s.sectionTitle}>4. How We Use Your Data</h2>
        <ul>
          <li style={s.li}>Providing, operating, and improving the Vella platform.</li>
          <li style={s.li}>Sending SMS marketing campaigns on your behalf via Twilio.</li>
          <li style={s.li}>Processing subscription payments via Stripe.</li>
          <li style={s.li}>Delivering AI-generated responses and creative content.</li>
          <li style={s.li}>Sending you platform notifications and support communications.</li>
          <li style={s.li}>Complying with legal obligations and enforcing our Terms of Service.</li>
          <li style={s.li}>Detecting and preventing fraud or abuse.</li>
        </ul>

        <h2 style={s.sectionTitle}>5. Third-Party Processors</h2>
        <p style={s.p}>We share your data only with the vendors below, who are contractually bound to protect it:</p>
        <div style={s.grid}>
          {[
            ['Twilio', 'SMS delivery & phone number routing'],
            ['ElevenLabs', 'Voice synthesis & audio processing'],
            ['Anthropic', 'AI reasoning (Claude models)'],
            ['OpenAI', 'AI content generation (optional)'],
            ['Stripe', 'Subscription billing & payment processing'],
            ['Supabase', 'Database hosting & authentication'],
            ['Railway', 'Backend API server hosting'],
            ['Vercel', 'Frontend web hosting (vella.amplifx.com)'],
          ].map(([name, role]) => (
            <div key={name} style={s.gridItem}>
              <div style={s.gridLabel}>{name}</div>
              <div>{role}</div>
            </div>
          ))}
        </div>
        <p style={{ ...s.p, marginTop: '1rem' }}>We do not sell, rent, or trade your personal data to any third party for advertising purposes.</p>

        <h2 style={s.sectionTitle}>6. Data Retention</h2>
        <p style={s.p}>
          We retain your account and campaign data for <strong>90 days</strong> after account deletion or subscription cancellation. Aggregated, de-identified analytics may be retained longer. Voice session data is ephemeral and not retained beyond the active session unless you save a transcript. You may request immediate deletion of all your data (see Section 8).
        </p>

        <h2 style={s.sectionTitle}>7. Security</h2>
        <p style={s.p}>
          All data is encrypted in transit (TLS 1.2+) and at rest. Access to production databases is restricted to authorized personnel. We conduct periodic security reviews. No system is 100% secure; notify us immediately at <span style={s.em}>team@amplifx.com</span> if you suspect a breach.
        </p>

        <h2 style={s.sectionTitle}>8. Your Rights (GDPR &amp; CCPA)</h2>
        <p style={s.p}>Depending on your location, you have the following rights. To exercise any of them, email <span style={s.em}>team@amplifx.com</span> with the subject "Privacy Request."</p>
        <ul>
          <li style={s.li}><span style={s.em}>Access</span> — request a copy of all personal data we hold about you.</li>
          <li style={s.li}><span style={s.em}>Correction</span> — request correction of inaccurate data.</li>
          <li style={s.li}><span style={s.em}>Deletion</span> — request permanent deletion of your account and personal data within 30 days. We will confirm deletion in writing.</li>
          <li style={s.li}><span style={s.em}>Portability</span> — receive your data in a machine-readable format (JSON or CSV).</li>
          <li style={s.li}><span style={s.em}>Opt-out of sale</span> — we do not sell personal data, so this right is already satisfied.</li>
          <li style={s.li}><span style={s.em}>Withdraw consent</span> — you may withdraw consent for voice recording by disabling microphone access in your browser at any time.</li>
        </ul>
        <p style={s.p}>We respond to all privacy requests within <strong>30 days</strong>. California residents may also contact the California Attorney General's office. EU/EEA residents may contact their local data protection authority.</p>

        <h2 style={s.sectionTitle}>9. Cookies</h2>
        <p style={s.p}>We use only essential cookies for session authentication (via Supabase). We do not use third-party advertising cookies or tracking pixels.</p>

        <h2 style={s.sectionTitle}>10. Children's Privacy</h2>
        <p style={s.p}>The Service is not directed to anyone under 18. We do not knowingly collect data from minors. Contact us immediately if you believe a minor's data has been collected.</p>

        <h2 style={s.sectionTitle}>11. Changes to This Policy</h2>
        <p style={s.p}>We will notify you of material changes via email or an in-app banner at least 7 days before they take effect. Continued use of the Service after the effective date constitutes acceptance.</p>

        <h2 style={s.sectionTitle}>12. Contact</h2>
        <div style={s.box}>
          <strong>Mancini Business Consulting LLC</strong><br />
          Raleigh, NC<br />
          Email: <span style={s.em}>team@amplifx.com</span>
        </div>

        <div style={s.footer}>
          © 2026 Mancini Business Consulting LLC. All rights reserved.
        </div>
    </div>
  );

  return embedded ? inner : <div style={s.container}>{inner}</div>;
};

export default PrivacyPolicy;
